import React, { useState } from 'react';

interface Props {
  tokenInfo: { status: 'existing_account' | 'new_account'; email: string; display_name?: string; message: string };
  isProcessing: boolean;
  error: string | null;
  onSubmit: (password: string) => Promise<boolean>;
  onCancel: () => void;
}

export default function BridgePasswordModal({ tokenInfo, isProcessing, error, onSubmit, onCancel }: Props) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState('');
  const isNew = tokenInfo.status === 'new_account';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    if (isNew && password !== confirm) { setLocalError('Passwords do not match'); return; }
    if (password.length < 6) { setLocalError('Password must be at least 6 characters'); return; }
    await onSubmit(password);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-xl">♥</div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Welcome to VitalQuant</h2>
            <p className="text-sm text-gray-500">{isNew ? 'Create your account' : 'Sign in to continue'}</p>
          </div>
        </div>

        <div className="bg-emerald-50 rounded-lg px-4 py-3 mb-6">
          <p className="text-sm text-emerald-800">{tokenInfo.message}</p>
          <p className="text-sm font-semibold text-emerald-900 mt-1">{tokenInfo.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isNew ? 'Create a password' : 'Enter your password'}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>
          {isNew && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm password"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
          )}

          {(localError || error) && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{localError || error}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors font-medium"
            >
              {isProcessing ? 'Processing...' : isNew ? 'Create Account' : 'Sign In'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
